<template>
    <Dialog
        v-model:visible="visivel"
        modal
        :draggable="false"
        :style="{ width: '46rem' }"
        :breakpoints="{ '960px': '90vw' }"
        class="novidades-dialog"
        @hide="confirmarLeitura"
    >
        <template #header>
            <div class="flex align-items-center gap-2">
                <i class="pi pi-megaphone text-xl novidades-icone-header"></i>
                <span class="text-xl font-semibold">{{ release.titulo }}</span>
                <Tag v-if="release.versao" :value="`v${release.versao}`" severity="warn" class="ml-1" />
            </div>
        </template>

        <div class="novidades-banner flex align-items-center justify-content-center py-2 mb-3 border-round">
            <!--
                Ilustração inline (não é <img>): acompanha o tema claro/escuro pelas
                CSS vars abaixo e não gera request extra nem asset para versionar.
            -->
            <svg class="novidades-ilustracao" viewBox="0 0 280 180" role="img"
                 aria-label="Ilustração de foguete decolando de uma janela do sistema">
                <defs>
                    <path id="novidadesEstrela"
                          d="M0 -10 C0 -3 3 0 10 0 C3 0 0 3 0 10 C0 3 -3 0 -10 0 C-3 0 0 -3 0 -10 Z" />
                </defs>

                <ellipse cx="140" cy="152" rx="96" ry="11" class="nov-sombra" />

                <rect x="34" y="26" width="212" height="116" rx="12" class="nov-janela" />
                <path d="M34 38 a12 12 0 0 1 12 -12 h188 a12 12 0 0 1 12 12 v10 H34 Z" class="nov-janela-barra" />
                <circle cx="49" cy="37" r="3.2" class="nov-ponto" />
                <circle cx="60" cy="37" r="3.2" class="nov-ponto" />
                <circle cx="71" cy="37" r="3.2" class="nov-ponto" />

                <path d="M131 130 C131 122 136 116 140 108 C144 116 149 122 149 130 C146 133 134 133 131 130 Z"
                      class="nov-chama-externa" />
                <path d="M136 126 C136 121 138 117 140 112 C142 117 144 121 144 126 C142 128 138 128 136 126 Z"
                      class="nov-chama-interna" />

                <path d="M120 88 C110 97 105 107 104 116 L120 108 Z" class="nov-aleta" />
                <path d="M160 88 C170 97 175 107 176 116 L160 108 Z" class="nov-aleta" />

                <path d="M140 50 C152 63 160 78 160 93 L160 108 L120 108 L120 93 C120 78 128 63 140 50 Z"
                      class="nov-foguete" />
                <path d="M140 50 C134 56 130 64 128 73 L128 108 L120 108 L120 93 C120 78 128 63 140 50 Z"
                      class="nov-foguete-luz" />
                <path d="M140 50 C146 56 151 64 154 73 L126 73 C129 64 134 56 140 50 Z" class="nov-nariz" />
                <circle cx="140" cy="88" r="9" class="nov-escotilha" />
                <circle cx="140" cy="88" r="5" class="nov-escotilha-brilho" />
                <rect x="123" y="102" width="34" height="6" rx="3" class="nov-nariz" />

                <use href="#novidadesEstrela" transform="translate(22,62) scale(0.85)" class="nov-estrela" />
                <use href="#novidadesEstrela" transform="translate(258,44) scale(0.6)" class="nov-estrela" />
                <use href="#novidadesEstrela" transform="translate(250,116) scale(0.9)" class="nov-estrela" />
                <use href="#novidadesEstrela" transform="translate(28,16) scale(0.5)" class="nov-estrela" />
            </svg>
        </div>

        <p v-if="release.subtitulo" class="text-center text-color-secondary mt-0 mb-3">
            {{ release.subtitulo }}
        </p>

        <div class="grid">
            <div v-for="(item, index) in release.itens" :key="index" class="col-12 md:col-6">
                <div class="novidades-item flex gap-3 p-3 h-full border-round align-items-start">
                    <span class="novidades-item-icone flex align-items-center justify-content-center border-circle flex-shrink-0">
                        <i :class="item.icone || 'pi pi-star'"></i>
                    </span>
                    <div>
                        <div class="font-semibold mb-1">{{ item.titulo }}</div>
                        <div class="text-sm text-color-secondary line-height-3">{{ item.descricao }}</div>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex align-items-center justify-content-between w-full">
                <small class="text-color-secondary">Este aviso não será exibido novamente.</small>
                <Button label="Entendi, vamos lá!" icon="pi pi-check" severity="success" size="small"
                        :loading="isSubmitting" @click="visivel = false" />
            </div>
        </template>
    </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Tag from 'primevue/tag';

import Novidade from '@/service/novidade.js';

export default {
    components: {
        Dialog,
        Button,
        Tag,
    },
    data() {
        return {
            visivel: false,
            isSubmitting: false,
            // Evita um segundo POST /ler: `@hide` dispara tanto no botão quanto
            // no X e no clique fora, e o botão já fecha via `visivel = false`.
            leituraEnviada: false,
            release: {
                versao: null,
                titulo: '',
                subtitulo: '',
                itens: [],
            },
        };
    },
    mounted() {
        this.carregar();
    },
    methods: {
        carregar() {
            Novidade.release()
                .then(response => {
                    const data = response.data ?? {};
                    if (!data.exibir || !(data.itens || []).length) {
                        return;
                    }

                    this.release = {
                        versao: data.versao,
                        titulo: data.titulo || 'Novidades do sistema',
                        subtitulo: data.subtitulo || '',
                        itens: data.itens || [],
                    };
                    this.visivel = true;
                })
                .catch(() => {
                    // Silencioso de propósito: é um aviso informativo — falhar
                    // ao buscá-lo não deve poluir a tela do usuário com toast.
                });
        },
        confirmarLeitura() {
            if (this.leituraEnviada) {
                return;
            }
            this.leituraEnviada = true;
            this.isSubmitting = true;

            Novidade.ler()
                .catch(() => {
                    // Se a marcação falhar, o dialog volta no próximo acesso —
                    // é o comportamento seguro, não vale interromper o usuário.
                    this.leituraEnviada = false;
                })
                .finally(() => {
                    this.isSubmitting = false;
                });
        },
    },
};
</script>

<style scoped>
.novidades-icone-header {
    color: var(--primary-color);
}

.novidades-banner {
    background: linear-gradient(135deg, rgba(255, 152, 0, 0.12), rgba(255, 152, 0, 0.02));
}

.novidades-ilustracao {
    width: 100%;
    max-width: 220px;
    height: auto;
}

/* Paleta da ilustração — tema claro */
.nov-sombra          { fill: rgba(0, 0, 0, 0.06); }
.nov-janela          { fill: #ffffff; stroke: #e2e8f0; stroke-width: 2; }
.nov-janela-barra    { fill: #f1f5f9; }
.nov-ponto           { fill: #cbd5e1; }
.nov-foguete         { fill: #e2e8f0; }
.nov-foguete-luz     { fill: #f8fafc; }
.nov-escotilha       { fill: #90caf9; }
.nov-escotilha-brilho{ fill: #e3f2fd; }
.nov-aleta           { fill: #ef6c00; }
.nov-nariz           { fill: var(--primary-color); }
.nov-chama-externa   { fill: #ffb74d; }
.nov-chama-interna   { fill: #fff3e0; }
.nov-estrela         { fill: var(--primary-color); opacity: 0.55; }

/* Paleta da ilustração — tema escuro */
.my-app-dark .nov-sombra           { fill: rgba(255, 255, 255, 0.05); }
.my-app-dark .nov-janela           { fill: #1a1a1a; stroke: #2a2a2a; }
.my-app-dark .nov-janela-barra     { fill: #212121; }
.my-app-dark .nov-ponto            { fill: #424242; }
.my-app-dark .nov-foguete          { fill: #cfd8dc; }
.my-app-dark .nov-foguete-luz      { fill: #eceff1; }
.my-app-dark .nov-escotilha        { fill: #42a5f5; }
.my-app-dark .nov-escotilha-brilho { fill: #bbdefb; }
.my-app-dark .nov-chama-interna    { fill: #ffe0b2; }

.novidades-item {
    background: var(--surface-50);
    border: 1px solid var(--surface-border);
}

.novidades-item-icone {
    width: 2.5rem;
    height: 2.5rem;
    background: rgba(255, 152, 0, 0.14);
    color: var(--primary-color);
}
</style>
